import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { streakService } from '../../services/streakService';
import { avatarService } from '../../services/avatarService';
import Icon from '../AppIcon';


import { User, ChevronDown, Shield, Upload, LogOut, Sun, Moon, Flame, HelpCircle, FileText, Layers } from 'lucide-react';
import { HealthMiniRing } from '../../pages/dashboard/components/MemoryHealthCard';
import { verseService } from '../../services/supabaseService';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut, isAuthenticated, isDemoAdmin } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [streakData, setStreakData] = useState({ currentStreak: 0, isLoading: true });
  const [avatarSvg, setAvatarSvg] = useState(null);
  const [healthAvgDays, setHealthAvgDays] = useState(null);

  // SIMPLIFIED: Use AuthContext admin status directly
  const isAdmin = userProfile?.role === 'admin';

  // ENHANCED: Store auth context in window for adminService access with error handling
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.authContext = {
          user,
          userProfile,
          isDemoAdmin,
          isAdmin
        };
      }
    } catch (error) {
      console.warn('Failed to set window auth context:', error);
    }
    
    return () => {
      try {
        if (typeof window !== 'undefined') {
          delete window.authContext;
        }
      } catch (error) {
        // Ignore cleanup errors
      }
    };
  }, [user, userProfile, isDemoAdmin, isAdmin]);

  // Fetch user's actual streak from database
  useEffect(() => {
    const fetchStreak = async () => {
      if (!user?.id) {
        setStreakData({ currentStreak: 0, isLoading: false });
        return;
      }

      try {
        const { data, error } = await streakService?.getStreakInfo(user?.id);
        
        if (error) {
          console.error('Error fetching streak:', error);
          setStreakData({ currentStreak: 0, isLoading: false });
          return;
        }

        setStreakData({
          currentStreak: data?.currentStreak || 0,
          isLoading: false
        });
      } catch (error) {
        console.error('Error fetching streak:', error);
        setStreakData({ currentStreak: 0, isLoading: false });
      }
    };

    fetchStreak();
  }, [user?.id]);

  // Fetch user's avatar SVG from database
  useEffect(() => {
    const fetchAvatar = async () => {
      if (!user?.id) {
        setAvatarSvg(null);
        return;
      }

      try {
        const avatarData = await avatarService?.getUserAvatar(user?.id);
        
        if (avatarData?.avatar_svg) {
          setAvatarSvg(avatarData?.avatar_svg);
        }
      } catch (error) {
        console.error('Error fetching avatar:', error);
        setAvatarSvg(null);
      }
    };

    fetchAvatar();
  }, [user?.id]);

  // Fetch memory health avg days for the mini ring
  useEffect(() => {
    const fetchHealth = async () => {
      if (!user?.id) return;
      try {
        const { data } = await verseService?.getUserVerses(user?.id);
        const verses = data || [];
        const activeVerses = verses?.filter(v => {
          const phase = v?.currentPhase ?? v?.current_phase ?? 0;
          return phase >= 1 && phase <= 7;
        });
        if (activeVerses?.length === 0) return;
        const now = Date.now();
        const MS_PER_DAY = 86400000;
        const daysList = activeVerses?.map(v => {
          const lastReview = v?.lastReviewedAt || v?.last_reviewed_at;
          if (!lastReview) return 90;
          return Math.max(0, Math.floor((now - new Date(lastReview)?.getTime()) / MS_PER_DAY));
        });
        const avg = daysList?.reduce((s, d) => s + d, 0) / daysList?.length;
        setHealthAvgDays(Math.round(avg));
      } catch (e) {
        // silently fail
      }
    };
    fetchHealth();
  }, [user?.id]);

  const navigationItems = [
    {
      label: 'Home',
      path: '/dashboard',
      icon: 'Home',
      showLabel: true,
      tooltip: 'View your progress and daily motivation'
    },
    {
      label: 'Bible',
      path: '/bible-reader',
      icon: 'BookOpen',
      showLabel: true,
      tooltip: 'Browse and discover scripture passages'
    },
    {
      label: 'Verses',
      path: '/verse-library',
      icon: 'BookMarked',
      showLabel: true,
      tooltip: 'Manage your personal verse collection'
    },
    {
      label: 'Plans',
      path: '/bible-reading-plans',
      icon: 'Calendar',
      showLabel: true,
      tooltip: 'Follow structured Bible reading plans'
    },
  ];

  // Admin navigation items - only visible to admins
  const adminNavigationItems = [
    {
      label: 'Admin',
      path: '/admin-dashboard',
      icon: 'Shield',
      showLabel: false,
      tooltip: 'Admin dashboard and site management'
    }
  ];

  // ENHANCED: Ensure admin items are always included when user is admin
  const allNavigationItems = useMemo(() => {
    const baseItems = [...navigationItems];
    if (isAdmin) {
      baseItems?.push(...adminNavigationItems);
    }
    return baseItems;
  }, [isAdmin]);

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      console.log('Searching for:', searchQuery);
    }
  };

  const handleSearchToggle = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (isSearchExpanded) {
      setSearchQuery('');
    }
  };

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple logout attempts
    
    try {
      setIsLoggingOut(true);
      setIsProfileDropdownOpen(false);
      
      // Use the proper Supabase signOut function
      const { error } = await signOut();
      
      if (error) {
        console.error('Logout error:', error);
        // Still redirect even if there's an error to ensure user sees login page
      }
      
      // Navigate to login page after logout
      navigate('/login');
      
    } catch (error) {
      console.error('Logout failed:', error);
      // Still redirect to login page even if logout fails
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  // Get user display information
  const displayName = userProfile?.full_name 
    ? `${userProfile?.full_name}${userProfile?.username ? ` (${userProfile?.username})` : ''}`
    : userProfile?.username || user?.email?.split('@')?.[0] || 'User';
  const displayEmail = user?.email || 'No email';

  const getThemeIcon = () => {
    if (theme === 'dark') return <Sun size={15} />;
    if (theme === 'light') return <Moon size={15} />;
    // slate
    return <Layers size={15} />;
  };

  const getThemeTooltip = () => {
    if (theme === 'dark') return 'Switch to light theme';
    if (theme === 'light') return 'Switch to slate theme';
    return 'Switch to dark theme';
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background border-b border-border shadow-warm">
      <div className="flex items-center justify-between h-12 px-3 lg:px-5">
        {/* Logo Section */}
        <div className="flex items-center space-x-2">
          <Link to="/dashboard" className="flex items-center space-x-2 group">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg overflow-hidden group-hover:opacity-90 transition-opacity duration-200">
              <img
                src="/assets/images/fm-color-full-1779117773822.png"
                alt="Fortified Mind logo"
                style={{
                  width: '130%',
                  height: '122%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                  marginLeft: '-15%',
                  marginTop: '-11%'
                }}
              />
            </div>
            <span className="text-xl font-heading font-semibold text-foreground hidden sm:block">
              Fortified Mind
            </span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-0.5">
          {allNavigationItems?.map((item) => {
            const isActive = location?.pathname === item?.path;
            const isAdminItem = item?.path === '/admin-dashboard';
            return (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-warm'
                    : isAdminItem
                      ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-950/50' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={16} />
                {item?.showLabel && <span>{item?.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Search and Actions */}
        <div className="flex items-center space-x-1">

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            title={getThemeTooltip()}
            className="flex items-center justify-center w-7 h-7 rounded-lg text-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
          >
            {getThemeIcon()}
          </button>

          {/* Streak - Fire icon + number + "d" - clickable, navigates to verse-library */}
          <button
            onClick={() => navigate('/verse-library')}
            title="Go to Verse Library"
            className="hidden sm:flex items-center gap-1 px-2 py-1 bg-success/10 rounded-lg hover:bg-success/20 transition-colors cursor-pointer"
          >
            <Flame size={15} className="text-success shrink-0" />
            <span className="text-xs font-mono font-semibold text-success">
              {streakData?.isLoading ? '…' : `${streakData?.currentStreak}d`}
            </span>
            {healthAvgDays !== null && (
              <span title={`Memory health: avg ${healthAvgDays} days since review`}>
                <HealthMiniRing avgDays={healthAvgDays} size={18} />
              </span>
            )}
          </button>

          {/* User Profile & Admin Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                {avatarSvg ? (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    dangerouslySetInnerHTML={{ __html: avatarSvg }}
                  />
                ) : (
                  <User className="w-3.5 h-3.5 text-primary" />
                )}
              </div>
              <ChevronDown className="w-3 h-3 text-muted-foreground hidden sm:block" />
            </button>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-card rounded-lg shadow-lg border border-border py-2 z-50">
                {/* Admin Menu Items */}
                {isAdmin && (
                  <>
                    <Link
                      to="/admin-dashboard"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                    >
                      <Shield className="w-4 h-4 mr-3 text-primary" />
                      Admin Dashboard
                    </Link>
                    <Link
                      to="/bible-version-import"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                    >
                      <Upload className="w-4 h-4 mr-3 text-primary" />
                      Bible Import
                    </Link>
                    <div className="border-t border-border my-2"></div>
                  </>
                )}
                
                <Link
                  to="/user-profile"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <User className="w-4 h-4 mr-3" />
                  Profile Settings
                </Link>
                <Link
                  to="/tools/notes"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <FileText className="w-4 h-4 mr-3" />
                  Study Notes
                </Link>
                <Link
                  to="/security"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <Shield className="w-4 h-4 mr-3" />
                  Security & Privacy
                </Link>
                <Link
                  to="/tutorial"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <HelpCircle className="w-4 h-4 mr-3" />
                  Help & Tutorial
                </Link>
                <div className="border-t border-border my-2"></div>
                <button
                  onClick={handleLogout}
                  disabled={logoutLoading}
                  className="w-full text-left flex items-center px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                >
                  {logoutLoading ? (
                    <div className="w-4 h-4 mr-3 animate-spin rounded-full border-2 border-destructive/30 border-t-destructive" />
                  ) : (
                    <LogOut className="w-4 h-4 mr-3" />
                  )}
                  {logoutLoading ? 'Signing out...' : 'Sign Out'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Mobile Bottom Navigation - ENHANCED */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border">
        <nav className="flex items-center justify-around py-1">
          {allNavigationItems?.map((item) => {
            const isActive = location?.pathname === item?.path;
            const isAdminItem = item?.path === '/admin-dashboard';
            return (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-all duration-200 min-w-[40px] ${
                  isActive
                    ? 'text-primary'
                    : isAdminItem
                      ? 'text-orange-600 dark:text-orange-400' :'text-muted-foreground hover:text-foreground'
                }`}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={18} />
                {item?.showLabel && (
                  <span className="text-[10px] font-caption leading-none">{item?.label}</span>
                )}
              </Link>
            );
          })}

          {/* Streak indicator on mobile - clickable */}
          <button
            onClick={() => navigate('/verse-library')}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 min-w-[40px]"
            title="Go to Verse Library"
          >
            <Flame size={18} className="text-success" />
            <span className="text-[10px] font-mono font-semibold text-success leading-none">
              {streakData?.isLoading ? '…' : `${streakData?.currentStreak}d`}
            </span>
          </button>
          
          {/* Mobile Profile Link */}
          <Link
            to="/user-profile"
            className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-all duration-200 min-w-[40px] ${
              location?.pathname === '/user-profile' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
            title="User Profile"
          >
            <div className={`w-[18px] h-[18px] rounded-full flex items-center justify-center ${
              isAdmin
                ? 'bg-gradient-to-br from-orange-500 to-orange-600' :'bg-gradient-to-br from-primary to-primary/80'
            }`}>
              <Icon name={isAdmin ? "Shield" : "User"} size={10} color="var(--color-primary-foreground)" />
            </div>
            <span className="text-[10px] font-caption leading-none">Profile</span>
          </Link>
        </nav>
      </div>
      {/* Backdrop for dropdown */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40 bg-black/20" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  );
};

export default Header;